import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const STATUSES = ["Active", "Waitlisted", "Withdrawn", "Graduated", "Archived", "Inactive"];
const PROGRAMS = ["Infant Care", "Toddler Care", "Preschool", "Pre-K", "After School", "Full-Time", "Part-Time"];

/** Edits the legacy student form fields plus the extended profile details in one place. */
export const DetailsDialog = ({ open, onOpenChange, student, onSaved }) => {
  const [f, setF] = useState({});
  const [custom, setCustom] = useState([]);
  useEffect(() => {
    if (!open) return;
    setF({ ...student, custom_fields: student.custom_fields || {}, reason: "" });
    api.get("/students/meta").then(({ data }) => setCustom(data.custom_fields || []));
  }, [open, student]);

  const save = async () => {
    try {
      const base = { first_name: f.first_name, last_name: f.last_name, birthday: f.birthday || "", gender: f.gender || "", program: f.program,
        room: f.room, status: f.status, allergies: f.allergies || "", medications: f.medications || "", doctor: f.doctor || "", notes: f.notes || "",
        parent_email: f.parent_email || "", parent_name: f.parent_name || "", parent_phone: f.parent_phone || "",
        emergency_contact: f.emergency_contact || "", checkin_code: f.checkin_code || "" };
      await api.put(`/students/${student.id}`, base);
      await api.put(`/students/${student.id}/details`, { preferred_name: f.preferred_name || "", pronouns: f.pronouns || "",
        enrollment_date: f.enrollment_date || "", expected_transition_date: f.expected_transition_date || "", status: f.status,
        notes: f.notes || "", custom_fields: f.custom_fields || {}, reason: f.reason || "" });
      toast.success("Student details saved"); onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="details-dialog">
        <DialogHeader><DialogTitle className="font-display">Edit student details</DialogTitle><DialogDescription>Changes are recorded in the audit history.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["first_name", "Legal first name", "text", true], ["last_name", "Legal last name", "text", true], ["preferred_name", "Preferred name"], ["pronouns", "Pronouns"],
            ["birthday", "Date of birth", "date"], ["gender", "Gender"], ["enrollment_date", "Enrollment date", "date"], ["expected_transition_date", "Expected graduation / transition", "date"],
            ["checkin_code", "Check-in code"], ["doctor", "Doctor"], ["parent_name", "Parent name"], ["parent_email", "Parent email", "email"], ["parent_phone", "Parent phone"], ["emergency_contact", "Emergency contact"]].map(([k, label, type, req]) => (
            <div key={k}><Label className="text-sm font-bold">{label}{req && <span className="text-[#EF4444]"> *</span>}</Label>
              <Input type={type || "text"} value={f[k] || ""} onChange={set(k)} className="mt-2 h-11 rounded-xl" data-testid={`details-${k}`} /></div>
          ))}
          {[["program", PROGRAMS], ["status", STATUSES]].map(([k, opts]) => (
            <div key={k}><Label className="text-sm font-bold capitalize">{k}</Label>
              <Select value={f[k] || opts[0]} onValueChange={(v) => setF({ ...f, [k]: v })}>
                <SelectTrigger className="mt-2 h-11 rounded-xl" data-testid={`details-${k}`}><SelectValue /></SelectTrigger>
                <SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></div>
          ))}
          {custom.map((c) => (
            <div key={c.key}><Label className="text-sm font-bold">{c.label}</Label>
              <Input value={f.custom_fields?.[c.key] || ""} onChange={(e) => setF({ ...f, custom_fields: { ...f.custom_fields, [c.key]: e.target.value } })} className="mt-2 h-11 rounded-xl" data-testid={`details-custom-${c.key}`} /></div>
          ))}
          <div className="sm:col-span-2"><Label className="text-sm font-bold">Notes</Label><Textarea value={f.notes || ""} onChange={set("notes")} className="mt-2 rounded-xl" data-testid="details-notes" /></div>
          <div className="sm:col-span-2"><Label className="text-sm font-bold">Reason for change (optional)</Label><Input value={f.reason || ""} onChange={set("reason")} className="mt-2 h-11 rounded-xl" data-testid="details-reason" /></div>
        </div>
        <p className="text-xs text-muted-foreground">Classroom moves are made from the Classroom & Schedule tab so the reason and history are captured.</p>
        <Button onClick={save} disabled={!f.first_name || !f.last_name} className="w-full h-12 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="details-save-btn">Save details</Button>
      </DialogContent>
    </Dialog>
  );
};
